import React, { useState, useRef, useEffect, useCallback } from 'react';

interface TerminalLine {
    id: string;
    type: 'input' | 'stdout' | 'stderr' | 'system' | 'error' | 'pty_output';
    content: string;
    timestamp: string;
}

interface InteractiveTerminalWebSocketProps {
    addLog?: (level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | 'AI_ACTION', message: string, component?: string) => void;
}

export const InteractiveTerminalWebSocket: React.FC<InteractiveTerminalWebSocketProps> = ({ addLog }) => {
    const [lines, setLines] = useState<TerminalLine[]>([]);
    const [input, setInput] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [workingDir, setWorkingDir] = useState('');
    const [commandHistory, setCommandHistory] = useState<string[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isExecuting, setIsExecuting] = useState(false);

    const wsRef = useRef<WebSocket | null>(null);
    const outputRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom
    useEffect(() => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [lines]);

    // Focus input on click
    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    // Initialize WebSocket connection
    const initializeWebSocket = useCallback(async () => {
        try {
            const apiKey = localStorage.getItem('omnigate_api_key') || '';
            if (!apiKey) {
                setLines([{
                    id: `err_${Date.now()}`,
                    type: 'error',
                    content: '✗ No API key found. Please login first.',
                    timestamp: new Date().toISOString()
                }]);
                addLog?.('ERROR', 'Terminal: No API key in localStorage', 'InteractiveTerminalWebSocket');
                return;
            }

            // Create WebSocket connection
            const websocket = new WebSocket('ws://localhost:8888/terminal/stream/ws');
            wsRef.current = websocket;

            websocket.onopen = () => {
                setIsConnected(true);
                addLog?.('INFO', 'WebSocket connection established', 'InteractiveTerminalWebSocket');

                // Initialize PTY session
                websocket.send(JSON.stringify({
                    type: 'init_session',
                    working_dir: workingDir || undefined,
                    api_key: apiKey
                }));

                setLines([{
                    id: `sys_${Date.now()}`,
                    type: 'system',
                    content: '✓ WebSocket connected - Real-time terminal ready',
                    timestamp: new Date().toISOString()
                }]);
            };

            websocket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    switch (data.type) {
                        case 'session_created':
                            setSessionId(data.session_id);
                            setWorkingDir(data.working_dir || '');
                            setLines(prev => [...prev, {
                                id: `sys_${Date.now()}`,
                                type: 'system',
                                content: `✓ PTY session started: ${data.session_id}`,
                                timestamp: new Date().toISOString()
                            }]);
                            break;

                        case 'pty_output':
                            setLines(prev => [...prev, {
                                id: `out_${Date.now()}`,
                                type: 'pty_output',
                                content: data.content,
                                timestamp: new Date().toISOString()
                            }]);
                            break;

                        case 'error':
                            setLines(prev => [...prev, {
                                id: `err_${Date.now()}`,
                                type: 'error',
                                content: `✗ ${data.message}`,
                                timestamp: new Date().toISOString()
                            }]);
                            break;

                        case 'working_dir_updated':
                            setWorkingDir(data.working_dir);
                            break;
                    }
                } catch (error) {
                    // Raw text output (fallback)
                    setLines(prev => [...prev, {
                        id: `out_${Date.now()}`,
                        type: 'stdout',
                        content: event.data,
                        timestamp: new Date().toISOString()
                    }]);
                }
            };

            websocket.onclose = () => {
                setIsConnected(false);
                setSessionId(null);
                setLines(prev => [...prev, {
                    id: `sys_${Date.now()}`,
                    type: 'system',
                    content: '✗ WebSocket disconnected',
                    timestamp: new Date().toISOString()
                }]);
                addLog?.('WARN', 'WebSocket connection closed', 'InteractiveTerminalWebSocket');
            };

            websocket.onerror = (error) => {
                setLines(prev => [...prev, {
                    id: `err_${Date.now()}`,
                    type: 'error',
                    content: '✗ WebSocket connection error',
                    timestamp: new Date().toISOString()
                }]);
                addLog?.('ERROR', 'WebSocket connection error', 'InteractiveTerminalWebSocket');
            };

        } catch (error: any) {
            setLines([{
                id: `err_${Date.now()}`,
                type: 'error',
                content: `✗ WebSocket setup failed: ${error.message}`,
                timestamp: new Date().toISOString()
            }]);
            addLog?.('ERROR', `WebSocket setup failed: ${error.message}`, 'InteractiveTerminalWebSocket');
        }
    }, [workingDir, addLog]);

    // Start WebSocket on mount
    useEffect(() => {
        initializeWebSocket();

        return () => {
            // Cleanup on unmount
            if (wsRef.current) {
                wsRef.current.close();
            }
            if (sessionId) {
                // Close PTY session
                const apiKey = localStorage.getItem('omnigate_api_key') || '';
                fetch(`http://localhost:8888/api/terminal/session/${sessionId}/close`, {
                    method: 'POST',
                    headers: { 'X-API-Key': apiKey }
                }).catch(() => { });
            }
        };
    }, []);

    // Execute command via WebSocket
    const executeCommand = async (cmd: string) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !cmd.trim()) {
            return;
        }

        setIsExecuting(true);
        setCommandHistory(prev => [...prev, cmd]);
        setHistoryIndex(-1);

        // Add input line
        setLines(prev => [...prev, {
            id: `input_${Date.now()}`,
            type: 'input',
            content: `$ ${cmd}`,
            timestamp: new Date().toISOString()
        }]);

        try {
            // Send command via WebSocket
            wsRef.current.send(JSON.stringify({
                type: 'execute',
                command: cmd,
                session_id: sessionId
            }));

        } catch (error: any) {
            setLines(prev => [...prev, {
                id: `err_${Date.now()}`,
                type: 'error',
                content: `✗ Command failed: ${error.message}`,
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsExecuting(false);
            setInput('');
            setTimeout(() => inputRef.current?.focus(), 10);
        }
    };

    // Handle key press
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            executeCommand(input);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (commandHistory.length > 0 && historyIndex < commandHistory.length - 1) {
                const newIndex = historyIndex + 1;
                setHistoryIndex(newIndex);
                setInput(commandHistory[commandHistory.length - 1 - newIndex]);
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (historyIndex > 0) {
                const newIndex = historyIndex - 1;
                setHistoryIndex(newIndex);
                setInput(commandHistory[commandHistory.length - 1 - newIndex]);
            } else {
                setHistoryIndex(-1);
                setInput('');
            }
        } else if (e.key === 'l' && e.ctrlKey) {
            e.preventDefault();
            setLines([]);
        }
    };

    // Quick commands - rozszerzone o MCP i AI pipeline'y
    const quickCommands = [
        { label: 'pwd', icon: 'fa-folder', cmd: 'pwd' },
        { label: 'ls', icon: 'fa-list', cmd: 'ls -la' },
        { label: 'ps', icon: 'fa-tasks', cmd: 'ps aux | grep -E "(mcp|qwen|python)" | head -20' },
        { label: 'df', icon: 'fa-hdd', cmd: 'df -h' },
        { label: 'clear', icon: 'fa-broom', cmd: 'clear' },
        { label: 'MCP', icon: 'fa-plug', cmd: 'find /home/arielwisnie/ai-gatewav-v2 -name "*mcp*" -type d' },
        { label: 'Qwen', icon: 'fa-robot', cmd: 'which qwen || echo "Qwen not installed"' },
        { label: 'GPU', icon: 'fa-microchip', cmd: 'nvidia-smi' },
    ];

    const getLineColor = (type: string) => {
        switch (type) {
            case 'input': return 'text-cyan-400';
            case 'stdout': return 'text-slate-300';
            case 'stderr': return 'text-red-400';
            case 'system': return 'text-purple-400';
            case 'error': return 'text-red-500';
            case 'pty_output': return 'text-green-300'; // ANSI colors preserved
            default: return 'text-slate-400';
        }
    };

    const reconnectWebSocket = () => {
        if (wsRef.current) {
            wsRef.current.close();
        }
        setLines([]);
        setSessionId(null);
        setIsConnected(false);
        setTimeout(() => initializeWebSocket(), 500);
    };

    return (
        <div
            ref={containerRef}
            className="flex flex-col h-full bg-[#131722] border border-[#2a2e39] rounded-xl overflow-hidden"
            onClick={handleContainerClick}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 bg-[#1e222d] border-b border-[#2a2e39] shrink-0">
                <div className="flex items-center gap-2">
                    <i className="fas fa-terminal text-purple-500 text-sm"></i>
                    <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                        WebSocket Terminal (Real-time)
                    </span>
                    <span className={`inline-block w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                </div>
                <div className="flex items-center gap-2">
                    {sessionId && (
                        <span className="text-[9px] text-slate-500 font-mono">
                            {sessionId}
                        </span>
                    )}
                    {workingDir && (
                        <span className="text-[9px] text-slate-600 font-mono truncate max-w-[150px]">
                            {workingDir}
                        </span>
                    )}
                    <button
                        onClick={reconnectWebSocket}
                        className="text-[9px] text-slate-500 hover:text-purple-400 transition-colors"
                        title="Reconnect WebSocket"
                    >
                        <i className="fas fa-redo"></i>
                    </button>
                </div>
            </div>

            {/* Quick Commands Bar */}
            <div className="flex items-center gap-1 px-3 py-1.5 bg-[#1a1e2a] border-b border-[#2a2e39] shrink-0 overflow-x-auto">
                {quickCommands.map((qc) => (
                    <button
                        key={qc.label}
                        onClick={() => executeCommand(qc.cmd)}
                        disabled={isExecuting || !isConnected}
                        className="flex items-center gap-1 px-2 py-1 text-[9px] font-bold uppercase tracking-wider bg-[#2a2e39] hover:bg-purple-500/20 hover:text-purple-400 text-slate-400 rounded transition-all disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                    >
                        <i className={`fas ${qc.icon} text-[8px]`}></i>
                        {qc.label}
                    </button>
                ))}
            </div>

            {/* Output Area - FLEX 1 */}
            <div
                ref={outputRef}
                className="flex-1 overflow-y-auto overflow-x-hidden p-3 font-mono text-[10px] leading-relaxed custom-scrollbar"
                style={{ minHeight: 0 }}
            >
                {lines.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-600">
                        <i className="fas fa-terminal text-3xl mb-3 opacity-30"></i>
                        <p className="text-xs font-bold uppercase tracking-wider">Connecting via WebSocket...</p>
                    </div>
                )}
                {lines.map((line) => (
                    <div key={line.id} className={`whitespace-pre-wrap break-words ${getLineColor(line.type)}`}>
                        {line.type === 'pty_output' ? (
                            <pre className="font-mono text-[10px] leading-relaxed" style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                            }}>
                                {line.content}
                            </pre>
                        ) : (
                            <pre className="font-mono text-[10px] leading-relaxed" style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                            }}>
                                {line.content}
                            </pre>
                        )}
                    </div>
                ))}
                {isExecuting && (
                    <div className="text-yellow-400 animate-pulse">
                        <i className="fas fa-circle-notch fa-spin mr-1"></i>
                        Executing via WebSocket...
                    </div>
                )}
            </div>

            {/* Input Area - SHRINK 0 */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[#1e222d] border-t border-[#2a2e39] shrink-0">
                <span className="text-purple-500 font-mono text-xs font-bold">$</span>
                <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isConnected ? "Type command (WebSocket real-time)..." : "Connecting via WebSocket..."}
                    disabled={!isConnected || isExecuting}
                    className="flex-1 bg-[#0d1117] text-slate-300 text-xs font-mono border border-[#2a2e39] rounded-lg px-3 py-1.5 focus:outline-none focus:border-purple-500/60 transition-colors placeholder-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    autoComplete="off"
                    spellCheck={false}
                />
                <button
                    onClick={() => executeCommand(input)}
                    disabled={!isConnected || isExecuting || !input.trim()}
                    className="px-3 py-1.5 bg-purple-600/80 hover:bg-purple-500 text-white text-[9px] font-bold uppercase tracking-wider rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    <i className="fas fa-paper-plane"></i>
                </button>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-3 py-1 bg-[#0d1117] border-t border-[#2a2e39] text-[8px] text-slate-600 shrink-0">
                <div className="flex items-center gap-2">
                    <span>↑↓ History</span>
                    <span>Ctrl+L Clear</span>
                    <span>Enter Execute</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="text-slate-700">•</span>
                    <span>WebSocket Terminal v3</span>
                    <span className={`${isConnected ? 'text-green-500' : 'text-red-500'}`}>
                        {isConnected ? 'CONNECTED' : 'DISCONNECTED'}
                    </span>
                </div>
            </div>
        </div>
    );
};